import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ColumnWidthOutlined, ExpandOutlined } from '@ant-design/icons';
import {
  assignCatalogToAccount,
  connectCredentials,
  createContractorAccount,
  getCatalog,
  getConnectionFieldMapping,
  getContractorAccount,
  getPipeHistory,
  getPublishJob,
  listCatalogs,
  listContractorAccounts,
  listPipeConnections,
  listPublishJobs,
  revokeCredentials,
  setConnectionFieldMapping,
  setContractorVendor,
  setDefaultEnvironment,
  testCredentialsConnection,
  triggerPublish,
} from '../../services/contractorsHubApi';
import { getOrderGenieErpPricesBulk } from '../../services/orderXApi';
import { ConstantsPanel, MappingCanvas, useFieldMapping } from './PipesPanel';

const { Text } = Typography;

const ENVIRONMENTS = [
  { value: 'test', label: 'Test' },
  { value: 'prod', label: 'Prod' },
];

// Display-only mirror of connectors/service_titan.py's DEFAULT_*/PROD_*
// hosts -- shown as placeholder text so an unset Auth/API URL field still
// tells you what host it'll actually hit. The backend (base_urls_for_
// environment) remains the single source of truth for resolution.
const ENV_DEFAULT_URLS = {
  test: { auth: 'https://auth-integration.servicetitan.io', api: 'https://api-integration.servicetitan.io' },
  prod: { auth: 'https://auth.servicetitan.io', api: 'https://api.servicetitan.io' },
};

// Mirrors DESIGN.md's status-badge vocabulary: never rely on color alone --
// every badge below is a Tag with an explicit text label, not just a color.
const PUBLISH_STATUS_TAG = {
  success: { color: 'green', label: 'success' },
  partial: { color: 'orange', label: 'partial' },
  failed: { color: 'red', label: 'failed' },
  pending: { color: 'default', label: 'pending' },
  processing: { color: 'blue', label: 'processing' },
};

const CREDENTIAL_STATUS_TAG = {
  connected: { color: 'green', label: 'connected' },
  revoked: { color: 'red', label: 'revoked' },
  not_connected: { color: 'default', label: 'not connected' },
};

const PublishStatusTag = ({ status }) => {
  const info = PUBLISH_STATUS_TAG[status] || { color: 'default', label: status || 'never published' };
  return <Tag color={info.color}>{info.label}</Tag>;
};

const CredentialStatusTag = ({ status }) => {
  const info = CREDENTIAL_STATUS_TAG[status] || CREDENTIAL_STATUS_TAG.not_connected;
  return <Tag color={info.color}>{info.label}</Tag>;
};

const CreateAccountModal = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await createContractorAccount({ p21CustomerId: values.p21CustomerId, displayName: values.displayName });
      message.success('Contractor account created');
      form.resetFields();
      onCreated();
    } catch (err) {
      if (err?.errorFields) return; // form validation error, already shown inline
      message.error(err.message || 'Could not create contractor account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New Contractor Account"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Create"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="p21CustomerId"
          label="ERP Customer ID"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="e.g. cust-1" />
        </Form.Item>
        <Form.Item
          name="displayName"
          label="Display Name"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input placeholder="e.g. Mike's HVAC Services" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const FSM_PLATFORM = 'service_titan';

// Both of a contractor's Credential Sets (Test and Prod) for one pipe,
// keyed by environment -- an environment key is simply absent until a
// Credential Set has been saved for it.
const useConnections = (contractorAccountId) => {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listPipeConnections(contractorAccountId, FSM_PLATFORM);
      const byEnv = {};
      (rows || []).forEach((row) => {
        byEnv[row.environment] = row;
      });
      setConnections(byEnv);
    } catch (err) {
      message.error(err.message || 'Could not load connections');
    } finally {
      setLoading(false);
    }
  }, [contractorAccountId]);

  useEffect(() => {
    load();
  }, [load]);

  return { connections, loading, reload: load };
};

const CredentialsSection = ({ account, onChanged }) => {
  const [form] = Form.useForm();
  const [environment, setEnvironment] = useState('prod');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const { connections, reload } = useConnections(account.id);

  const current = connections[environment];
  const hasSavedSecret = Boolean(current);
  const [showEditForm, setShowEditForm] = useState(true);

  useEffect(() => {
    setShowEditForm(!hasSavedSecret);
  }, [hasSavedSecret, environment]);

  useEffect(() => {
    form.setFieldsValue({
      owner: current?.owner || 'tenant',
      clientId: current?.client_id || '',
      tenantId: current?.tenant_id || '',
      appKey: current?.app_key || '',
      authBaseUrl: current?.auth_base_url || '',
      apiBaseUrl: current?.api_base_url || '',
    });
  }, [form, current]);

  const refresh = async () => {
    await reload();
    onChanged();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await connectCredentials(account.id, {
        fsmPlatform: FSM_PLATFORM,
        environment,
        owner: values.owner,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        tenantId: values.tenantId,
        appKey: values.appKey,
        authBaseUrl: values.authBaseUrl,
        apiBaseUrl: values.apiBaseUrl,
      });
      message.success(`ServiceTitan ${environment} credentials saved`);
      setShowEditForm(false);
      await refresh();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      await testCredentialsConnection(account.id, FSM_PLATFORM, environment);
      message.success('Connection verified against ServiceTitan');
      await refresh();
    } catch (err) {
      message.error(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await revokeCredentials(account.id, FSM_PLATFORM, environment);
      message.success(`${environment} credentials revoked`);
      await refresh();
    } catch (err) {
      message.error(err.message || 'Could not revoke credentials');
    } finally {
      setRevoking(false);
    }
  };

  const handleSetDefault = async () => {
    setSettingDefault(true);
    try {
      await setDefaultEnvironment(account.id, FSM_PLATFORM, environment);
      message.success(`${environment} is now the default connection for publishing`);
      await refresh();
    } catch (err) {
      message.error(err.message || 'Could not set default environment');
    } finally {
      setSettingDefault(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }} align="center">
        <Segmented
          value={environment}
          onChange={setEnvironment}
          options={ENVIRONMENTS.map((e) => ({
            ...e,
            label: connections[e.value]?.is_default ? `${e.label} (default)` : e.label,
          }))}
        />
        <CredentialStatusTag status={current?.status} />
        {!current?.is_default && hasSavedSecret && (
          <Button size="small" onClick={handleSetDefault} loading={settingDefault}>
            Set as default
          </Button>
        )}
      </Space>

      {hasSavedSecret && !showEditForm ? (
        <div>
          <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Client ID">{current.client_id}</Descriptions.Item>
            <Descriptions.Item label="Tenant ID">{current.tenant_id}</Descriptions.Item>
            <Descriptions.Item label="App Key">{current.app_key || '—'}</Descriptions.Item>
            <Descriptions.Item label="Client Secret">•••••••••••••••• (saved)</Descriptions.Item>
            <Descriptions.Item label="Auth URL">
              <Text code>{current.effective_auth_base_url}</Text>
              {!current.auth_base_url && (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                  (default for {environment})
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="API URL">
              <Text code>{current.effective_api_base_url}</Text>
              {!current.api_base_url && (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                  (default for {environment})
                </Text>
              )}
            </Descriptions.Item>
          </Descriptions>
          <Space>
            <Button type="primary" onClick={handleTestConnection} loading={testing}>
              Test Connection
            </Button>
            <Button onClick={() => setShowEditForm(true)}>Update Credentials</Button>
            <Button danger onClick={handleRevoke} loading={revoking}>
              Revoke
            </Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              "Test Connection" re-validates using the client secret already saved on the server -- it is a
              connector credential used by scheduled publish jobs, not a password you re-enter each time.
              Publishing against the {environment} environment always reads the credentials saved here,
              never anything typed in this UI.
            </Text>
          </div>
        </div>
      ) : (
        <div>
          <Form form={form} layout="vertical" initialValues={{ owner: 'tenant' }}>
            <Form.Item name="owner" label="Credential Ownership">
              <Select
                options={[
                  { value: 'tenant', label: 'Tenant holds credentials' },
                  { value: 'contractor', label: 'Contractor holds credentials' },
                ]}
              />
            </Form.Item>
            <Form.Item name="clientId" label="Client ID" rules={[{ required: true, message: 'Required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="clientSecret" label="Client Secret" rules={[{ required: true, message: 'Required' }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="tenantId" label="ServiceTitan Tenant ID" rules={[{ required: true, message: 'Required' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="appKey" label="App Key">
              <Input />
            </Form.Item>
            <Form.Item
              name="authBaseUrl"
              label="Auth Base URL"
              tooltip="Leave blank to use the standard host for this environment."
            >
              <Input placeholder={ENV_DEFAULT_URLS[environment].auth} />
            </Form.Item>
            <Form.Item
              name="apiBaseUrl"
              label="API Base URL"
              tooltip="Leave blank to use the standard host for this environment."
            >
              <Input placeholder={ENV_DEFAULT_URLS[environment].api} />
            </Form.Item>
          </Form>
          <Space>
            <Button type="primary" onClick={handleSave} loading={saving}>
              {hasSavedSecret ? 'Save' : 'Connect'}
            </Button>
            {hasSavedSecret && <Button onClick={() => setShowEditForm(false)}>Cancel</Button>}
          </Space>
        </div>
      )}
    </div>
  );
};

// This connection's (contractor + environment) Constants/Mapping override --
// reuses PipesPanel's Mapping/Constants tabs and their useFieldMapping hook
// unchanged, just pointed at the per-connection get/set endpoints instead of
// the tenant-wide ones. Falls back to the tenant-wide mapping (shown on the
// top-level Pipes tab) until this connection saves its own.
const ConnectionConstantsSection = ({ account }) => {
  const [environment, setEnvironment] = useState('prod');
  const fm = useFieldMapping({
    loadMapping: () => getConnectionFieldMapping(account.id, FSM_PLATFORM, environment),
    saveMapping: (mapping) => setConnectionFieldMapping(account.id, FSM_PLATFORM, environment, mapping),
    deps: [account.id, environment],
  });

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Text type="secondary">Environment</Text>
        <Segmented value={environment} onChange={setEnvironment} options={ENVIRONMENTS} />
      </Space>
      <Tabs
        size="small"
        items={[
          { key: 'mapping', label: 'Mapping', children: <MappingCanvas fm={fm} /> },
          { key: 'constants', label: 'Constants', children: <ConstantsPanel fm={fm} /> },
        ]}
      />
    </div>
  );
};

const PIPE_HISTORY_ACTION_LABEL = {
  credentials_connected: 'Credentials connected',
  credentials_revoked: 'Credentials revoked',
  credentials_tested: 'Connection tested',
  constants_updated: 'Constants updated',
  default_environment_changed: 'Default environment changed',
};

const PipeHistorySection = ({ account }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (nextPage = 1, nextPageSize = pageSize) => {
      setLoading(true);
      try {
        const result = await getPipeHistory(account.id, FSM_PLATFORM, { page: nextPage, pageSize: nextPageSize });
        setRows(result.items || []);
        setTotal(result.total || 0);
        setPage(result.page || nextPage);
        setPageSize(result.page_size || nextPageSize);
      } catch (err) {
        message.error(err.message || 'Could not load history');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account.id]
  );

  useEffect(() => {
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  return (
    <Table
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={rows}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        onChange: load,
        onShowSizeChange: load,
      }}
      locale={{ emptyText: <Empty description="No changes recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      expandable={{
        rowExpandable: (row) => Boolean(row.before || row.after),
        expandedRowRender: (row) => (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
            {JSON.stringify({ before: row.before, after: row.after }, null, 2)}
          </pre>
        ),
      }}
      columns={[
        { title: 'Action', dataIndex: 'action', render: (a) => PIPE_HISTORY_ACTION_LABEL[a] || a },
        {
          title: 'Environment',
          dataIndex: 'environment',
          width: 110,
          render: (env) => <Tag>{env}</Tag>,
        },
        { title: 'By', dataIndex: 'actor_email', render: (v) => v || <Text type="secondary">—</Text> },
        { title: 'When', dataIndex: 'created_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
      ]}
    />
  );
};

const VendorOverrideSection = ({ account, onChanged }) => {
  const [vendorId, setVendorId] = useState(account.vendor_id ?? undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVendorId(account.vendor_id ?? undefined);
  }, [account.vendor_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setContractorVendor(account.id, vendorId ?? null);
      message.success('Vendor ID saved for this contractor account');
      onChanged();
    } catch (err) {
      message.error(err.message || 'Could not save vendor override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      <Text strong>Vendor Override</Text>
      <div style={{ marginTop: 4, marginBottom: 8 }}>
        <Text type="secondary">
          Overrides the tenant-wide default Vendor ID (set under the top-level Pipes tab) for this
          contractor account's own ServiceTitan tenant. Leave blank to use the tenant default.
        </Text>
      </div>
      <Space>
        <InputNumber placeholder="Vendor ID" style={{ width: 220 }} value={vendorId} onChange={setVendorId} />
        <Button onClick={handleSave} loading={saving}>
          Save
        </Button>
      </Space>
    </div>
  );
};

const CatalogSection = ({ account, catalogs, onChanged }) => {
  const [selected, setSelected] = useState(account.catalog_master_id || undefined);
  const [assigning, setAssigning] = useState(false);

  // account is the same prop reference across different contractor accounts
  // (AccountDetailDrawer never remounts this component) -- without this, a
  // catalog assigned to account A stays selected in the dropdown after
  // switching to account B, or after a stale prior selection lingers here
  // once the server-confirmed assignment changes underneath us.
  useEffect(() => {
    setSelected(account.catalog_master_id || undefined);
  }, [account.id, account.catalog_master_id]);

  const handleAssign = async () => {
    setAssigning(true);
    try {
      await assignCatalogToAccount(account.id, selected);
      message.success('Catalog assigned');
      onChanged();
    } catch (err) {
      message.error(err.message || 'Could not assign catalog');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Text strong>Catalog</Text>
      <div style={{ marginTop: 8 }}>
        <Space>
          <Select
            style={{ width: 260 }}
            placeholder="No catalog assigned yet"
            value={selected}
            onChange={setSelected}
            options={catalogs.map((c) => ({
              value: c.master_id,
              label: `${c.display_name} (v${c.current_version_number || '-'}, ${c.item_count} items)`,
            }))}
          />
          <Button onClick={handleAssign} loading={assigning} disabled={!selected}>
            Assign
          </Button>
        </Space>
      </div>
    </div>
  );
};

const DEFAULT_JOBS_PAGE_SIZE = 20;

const usePublishJobs = (accountId) => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_JOBS_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const loadJobs = useCallback(
    async (nextPage = page, nextPageSize = pageSize) => {
      if (!accountId) {
        setJobs([]);
        setTotal(0);
        return;
      }
      setLoading(true);
      try {
        const result = await listPublishJobs(accountId, { page: nextPage, pageSize: nextPageSize });
        setJobs(result.items || []);
        setTotal(result.total || 0);
        setPage(result.page || nextPage);
        setPageSize(result.page_size || nextPageSize);
      } catch (err) {
        // Non-fatal for the detail view -- history is supplementary.
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId]
  );

  useEffect(() => {
    loadJobs(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const changePage = useCallback(
    (nextPage, nextPageSize) => loadJobs(nextPage, nextPageSize),
    [loadJobs]
  );

  const reload = useCallback(() => loadJobs(1, pageSize), [loadJobs, pageSize]);

  return { jobs, total, page, pageSize, loading, reload, changePage };
};

// Publish jobs run in a background executor now (publish_jobs.py) instead of
// blocking the request -- poll GET /publish-jobs/{job_id} at this interval
// to drive the progress bar until the job reaches a terminal status.
const PUBLISH_POLL_INTERVAL_MS = 1500;
const PUBLISH_TERMINAL_STATUSES = ['success', 'partial', 'failed'];

const PublishActionSection = ({ account, catalogs, jobs, onPublished, onChanged }) => {
  const [publishing, setPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(null);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [environment, setEnvironment] = useState(account.default_environment || 'prod');
  const { connections } = useConnections(account.id);
  const pollRef = useRef(null);

  useEffect(() => {
    setEnvironment(account.default_environment || 'prod');
  }, [account.id, account.default_environment]);

  // Stop polling if this row unmounts (e.g. account list re-renders) mid-publish.
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const assignedCatalog = catalogs.find((c) => c.master_id === account.catalog_master_id);
  const selectedConnection = connections[environment];
  const canPublish = selectedConnection?.status === 'connected' && Boolean(assignedCatalog?.current_version_id);
  const lastJob = jobs[0];

  const finishPublish = async (job) => {
    if (job.status === 'success') message.success('Published successfully');
    else if (job.status === 'partial') message.warning('Published with some items rejected');
    else message.error('Publish failed');
    setPublishing(false);
    await onPublished();
    onChanged();
  };

  const pollPublishJob = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const job = await getPublishJob(jobId);
        setPublishProgress(job);
        if (PUBLISH_TERMINAL_STATUSES.includes(job.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await finishPublish(job);
        }
      } catch (err) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPublishing(false);
        message.error(err.message || 'Could not fetch publish status');
      }
    }, PUBLISH_POLL_INTERVAL_MS);
  };

  const handlePublish = async () => {
    setPublishing(true);
    setPublishProgress(null);
    try {
      // Price the catalog's current items as this contractor (its ERP
      // customer id) through the same connector pricing call OrderX uses,
      // then publish exactly that version with those prices.
      setFetchingPrices(true);
      const catalog = await getCatalog(assignedCatalog.master_id);
      const skus = (catalog.items || []).map((item) => item.sku).filter(Boolean);
      const priceResp = skus.length
        ? await getOrderGenieErpPricesBulk(skus, { customer_id_erp: account.p21_customer_id })
        : {};
      setFetchingPrices(false);
      const prices = priceResp?.prices && typeof priceResp.prices === 'object' ? priceResp.prices : {};
      const job = await triggerPublish(account.id, catalog.version_id, environment, prices);
      setPublishProgress(job);
      if (PUBLISH_TERMINAL_STATUSES.includes(job.status)) {
        await finishPublish(job);
        return;
      }
      pollPublishJob(job.id);
    } catch (err) {
      message.error(err.message || 'Could not trigger publish');
      setFetchingPrices(false);
      setPublishing(false);
    }
  };

  const progressPercent = publishProgress?.total_count
    ? Math.min(100, Math.round((publishProgress.processed_count / publishProgress.total_count) * 100))
    : 0;
  const progressStatus = publishProgress?.status === 'failed' ? 'exception' : progressPercent >= 100 ? 'success' : 'active';

  return (
    <div>
      <Space align="center" style={{ marginBottom: 12 }}>
        <Text strong>Publish to ServiceTitan</Text>
        <PublishStatusTag status={account.last_publish_status} />
      </Space>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
          Environment
        </Text>
        <Segmented
          value={environment}
          onChange={setEnvironment}
          options={ENVIRONMENTS.map((e) => ({
            ...e,
            label: connections[e.value]?.is_default ? `${e.label} (default)` : e.label,
          }))}
        />
      </div>
      <div>
        <Button type="primary" onClick={handlePublish} loading={publishing} disabled={!canPublish}>
          Publish Catalog
        </Button>
        {!canPublish && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              {selectedConnection?.status !== 'connected'
                ? `Connect ServiceTitan ${environment} credentials before publishing.`
                : 'Assign a catalog before publishing.'}
            </Text>
          </div>
        )}
      </div>
      {publishing && (
        <div style={{ marginTop: 12, maxWidth: 360 }}>
          <Progress percent={progressPercent} status={progressStatus} />
          <Text type="secondary">
            {fetchingPrices
              ? 'Fetching ERP prices...'
              : publishProgress?.total_count
                ? `${publishProgress.processed_count} of ${publishProgress.total_count} items published`
                : 'Starting publish...'}
          </Text>
        </div>
      )}
      {lastJob?.result_detail?.error && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message="Last publish failed"
          description={lastJob.result_detail.error}
        />
      )}
    </div>
  );
};

const JobDetailPanel = ({ job }) => {
  const detail = job.result_detail || {};
  if (detail.error) {
    // No connector call was even made (credentials/vendor/catalog resolution
    // failed before any request left this service) -- this is the whole log.
    return (
      <Alert
        type="error"
        showIcon
        message="Publish failed before reaching ServiceTitan"
        description={<Text code>{detail.error}</Text>}
      />
    );
  }
  const items = detail.items || [];
  const rejected = items.filter((i) => !i.success);
  if (!items.length) return <Text type="secondary">No per-item detail recorded for this job.</Text>;
  const erpPricing = detail.erp_pricing;
  const unpricedSkus = erpPricing?.unpriced_skus || [];
  return (
    <div>
      {erpPricing && (
        <Alert
          style={{ marginBottom: 12 }}
          type={unpricedSkus.length ? 'warning' : 'success'}
          showIcon
          message={`ERP pricing: ${erpPricing.priced_count} of ${items.length} items priced live`}
          description={
            unpricedSkus.length
              ? `Published with the catalog's stored customer price: ${unpricedSkus.join(', ')}`
              : undefined
          }
        />
      )}
      {detail.sample_payload?.body && (
        <Collapse
          size="small"
          style={{ marginBottom: 12 }}
          items={[
            {
              key: 'sample_payload',
              label: `Payload sent to ServiceTitan (SKU ${detail.sample_payload.sku}, ${detail.sample_payload.method})`,
              children: (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
                  {JSON.stringify(detail.sample_payload.body, null, 2)}
                </pre>
              ),
            },
          ]}
        />
      )}
      {rejected.length > 0 && (
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Data pipe response for rejected items
        </Text>
      )}
      <Table
        size="small"
        rowKey="sku"
        pagination={false}
        dataSource={items}
        columns={[
          { title: 'SKU', dataIndex: 'sku', width: 160 },
          {
            title: 'Result',
            dataIndex: 'success',
            width: 100,
            render: (success) => (success ? <Tag color="green">ok</Tag> : <Tag color="red">rejected</Tag>),
          },
          {
            title: 'ServiceTitan response',
            dataIndex: 'reason',
            render: (reason) => (reason ? <Text code>{reason}</Text> : <Text type="secondary">—</Text>),
          },
        ]}
      />
    </div>
  );
};

const PublishHistorySection = ({ jobs, total, page, pageSize, loading, onChangePage }) => {
  return (
    <Table
      size="small"
      rowKey="id"
      loading={loading}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        onChange: onChangePage,
        onShowSizeChange: onChangePage,
      }}
      locale={{ emptyText: <Empty description="No publishes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      dataSource={jobs}
      expandable={{
        rowExpandable: (job) =>
          Boolean(job.result_detail?.error || job.result_detail?.items?.length || job.result_detail?.sample_payload?.body),
        expandedRowRender: (job) => <JobDetailPanel job={job} />,
      }}
      columns={[
        {
          title: 'Catalog',
          dataIndex: 'catalog_display_name',
          render: (name, job) => (name ? `${name} (v${job.catalog_version_number})` : '—'),
        },
        { title: 'Status', dataIndex: 'status', render: (s) => <PublishStatusTag status={s} /> },
        { title: 'Environment', dataIndex: 'environment', width: 100, render: (env) => (env ? <Tag>{env}</Tag> : '—') },
        { title: 'Total', dataIndex: 'item_count', width: 80, align: 'right' },
        {
          title: 'Succeeded',
          dataIndex: 'succeeded_count',
          width: 100,
          align: 'right',
          render: (v) => <Text type="success">{v}</Text>,
        },
        {
          title: 'Failed',
          dataIndex: 'failed_count',
          width: 90,
          align: 'right',
          render: (v) => (v > 0 ? <Text type="danger">{v}</Text> : v),
        },
        {
          title: 'When',
          dataIndex: 'updated_at',
          render: (v) => (v ? new Date(v).toLocaleString() : '—'),
        },
      ]}
    />
  );
};

const MIN_DRAWER_WIDTH = 480;
const DEFAULT_DRAWER_WIDTH = 920;

const AccountDetailDrawer = ({ contractorAccountId, onClose, onChanged }) => {
  const [account, setAccount] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [error, setError] = useState('');
  const [width, setWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const resizingRef = useRef(false);

  const { jobs, total, page, pageSize, loading: jobsLoading, reload: reloadJobs, changePage: changeJobsPage } =
    usePublishJobs(contractorAccountId);

  // Always re-fetch both the account and the catalog list here rather than
  // trusting the panel-level `catalogs` snapshot (loaded once on page
  // mount) -- a catalog's current_version_id can change from other tabs or
  // other users, and publishing against a stale version_id silently ships
  // the wrong item set. Every open, and every mutation below, re-reads both.
  const load = useCallback(async () => {
    if (!contractorAccountId) {
      setAccount(null);
      setCatalogs([]);
      setError('');
      return;
    }
    try {
      const [accountData, catalogRows] = await Promise.all([
        getContractorAccount(contractorAccountId),
        listCatalogs(),
      ]);
      setAccount(accountData);
      setCatalogs(catalogRows);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load contractor account');
    }
  }, [contractorAccountId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (contractorAccountId) setWidth(Math.min(DEFAULT_DRAWER_WIDTH, window.innerWidth));
  }, [contractorAccountId]);

  const handleChanged = () => {
    load();
    onChanged();
  };

  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    const onMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const next = Math.min(window.innerWidth, Math.max(MIN_DRAWER_WIDTH, window.innerWidth - moveEvent.clientX));
      setWidth(next);
    };
    const onMouseUp = () => {
      resizingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const toggleFullWidth = () => {
    setWidth((w) => (w >= window.innerWidth ? DEFAULT_DRAWER_WIDTH : window.innerWidth));
  };

  return (
    <Drawer
      title={account ? account.display_name : 'Contractor Account'}
      width={width}
      open={Boolean(contractorAccountId)}
      onClose={onClose}
      destroyOnClose
      extra={
        <Button
          size="small"
          icon={<ExpandOutlined />}
          onClick={toggleFullWidth}
          title="Toggle full width"
        >
          {width >= window.innerWidth ? 'Restore width' : 'Full width'}
        </Button>
      }
      styles={{ wrapper: { overflow: 'visible' } }}
    >
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        style={{
          position: 'absolute',
          left: -6,
          top: 0,
          bottom: 0,
          width: 12,
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        <ColumnWidthOutlined style={{ opacity: 0.35 }} />
      </div>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      {account && (
        <>
          <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="ERP Customer ID">{account.p21_customer_id}</Descriptions.Item>
          </Descriptions>
          <CatalogSection account={account} catalogs={catalogs} onChanged={handleChanged} />
          <Tabs
            defaultActiveKey="publish"
            items={[
              {
                key: 'publish',
                label: 'Publish',
                children: (
                  <PublishActionSection
                    account={account}
                    catalogs={catalogs}
                    jobs={jobs}
                    onPublished={reloadJobs}
                    onChanged={handleChanged}
                  />
                ),
              },
              {
                key: 'history',
                label: 'Publish History',
                children: (
                  <PublishHistorySection
                    jobs={jobs}
                    total={total}
                    page={page}
                    pageSize={pageSize}
                    loading={jobsLoading}
                    onChangePage={changeJobsPage}
                  />
                ),
              },
              {
                key: 'pipes',
                label: 'Data Pipes',
                children: (
                  <Collapse
                    defaultActiveKey={['service_titan']}
                    items={[
                      {
                        key: 'service_titan',
                        label: (
                          <Space>
                            <span>ServiceTitan</span>
                            <CredentialStatusTag status={account.credential_status} />
                          </Space>
                        ),
                        children: (
                          <Tabs
                            size="small"
                            defaultActiveKey="connections"
                            items={[
                              {
                                key: 'connections',
                                label: 'Connections',
                                children: (
                                  <>
                                    <CredentialsSection account={account} onChanged={handleChanged} />
                                    <VendorOverrideSection account={account} onChanged={handleChanged} />
                                  </>
                                ),
                              },
                              {
                                key: 'constants',
                                label: 'Constants',
                                children: <ConnectionConstantsSection account={account} />,
                              },
                              {
                                key: 'history',
                                label: 'History',
                                children: <PipeHistorySection account={account} />,
                              },
                            ]}
                          />
                        ),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </Drawer>
  );
};

const ContractorAccountsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const accountRows = await listContractorAccounts();
      setRows(accountRows);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load contractor accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          New Contractor Account
        </Button>
      </Space>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <Empty description="No contractor accounts yet" /> }}
        onRow={(row) => ({ onClick: () => setSelectedId(row.id), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Display Name', dataIndex: 'display_name' },
          { title: 'ERP Customer ID', dataIndex: 'p21_customer_id' },
          {
            title: 'ServiceTitan',
            dataIndex: 'credential_status',
            render: (status) => <CredentialStatusTag status={status} />,
          },
          {
            title: 'Last Publish',
            dataIndex: 'last_publish_status',
            render: (status) => <PublishStatusTag status={status} />,
          },
        ]}
      />
      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />
      <AccountDetailDrawer
        contractorAccountId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
};

export default ContractorAccountsPanel;
